import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Buildings, ArrowSquareOut, X } from "@phosphor-icons/react";
import { usePortal } from "./usePortal";

/* Main-client portal helpers: a "for client" chip on rows (links to that sub-client's oversight page), a client
   filter for lists (deep-linkable via ?client=<id> | __self__), and an "on behalf of" selector for the request /
   emergency / project forms. All render nothing for standalone and sub-client users (no sub_clients on me.client). */

const FOCUS_KEY = "portal.focusClient";
const focusListeners = new Set();
const readFocus = () => { try { return sessionStorage.getItem(FOCUS_KEY) || ""; } catch { return ""; } };
const writeFocus = (v) => {
  try { if (v) sessionStorage.setItem(FOCUS_KEY, v); else sessionStorage.removeItem(FOCUS_KEY); } catch { /* private mode */ }
  focusListeners.forEach((fn) => fn(v || ""));
};

/* Reactive view of the remembered focus (for chrome outside the filtered pages, e.g. the banner under the header). */
export function useFocusClient() {
  const [focus, setFocus] = useState(readFocus());
  useEffect(() => { focusListeners.add(setFocus); return () => focusListeners.delete(setFocus); }, []);
  return focus;
}

/* Slim bar under the portal header whenever a client focus is active: names the client, links to their overview,
   one-tap clear (also strips ?client= from the current URL). Renders nothing for non-main clients / no focus. */
export function FocusBanner() {
  const { client } = usePortal();
  const focus = useFocusClient();
  const [params, setParams] = useSearchParams();
  const subs = client?.sub_clients || [];
  if (!subs.length || !focus) return null;
  const sub = subs.find((s) => s.id === focus);
  if (focus !== "__self__" && !sub) return null;
  const name = sub ? sub.display_name : client?.display_name || "your own work";
  const clear = () => { writeFocus(""); if (params.has("client")) { const next = new URLSearchParams(params); next.delete("client"); setParams(next, { replace: true }); } };
  return (
    <div className="flex items-center gap-3 px-4 sm:px-6 py-1.5 text-xs border-b border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 text-sky-900 dark:text-sky-200" role="status" data-testid="focus-banner">
      <Buildings size={14} className="shrink-0" />
      <p className="truncate"><span className="font-semibold" data-testid="focus-banner-name">Viewing {name}</span><span className="hidden sm:inline text-sky-800/80 dark:text-sky-300/80"> — lists across the portal are limited to this client</span></p>
      <div className="ml-auto flex items-center gap-1.5 shrink-0">
        {sub && <Link to={`/portal/sub-clients/${sub.id}`} className="inline-flex items-center gap-1 h-6 px-2 rounded border border-sky-300 dark:border-sky-800 bg-white/70 dark:bg-transparent hover:bg-white" data-testid="focus-banner-overview"><ArrowSquareOut size={11} /> Overview</Link>}
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 h-6 px-2 rounded border border-sky-300 dark:border-sky-800 bg-white/70 dark:bg-transparent hover:bg-white" data-testid="focus-banner-clear"><X size={11} /> Clear</button>
      </div>
    </div>
  );
}

/* The chosen client sticks for the whole browsing session: ?client= in the URL wins (shareable), otherwise the remembered
   focus is restored and written back into the URL so it is always visible. Cleared by picking "All clients". */
export function useFamilyFilter() {
  const { client } = usePortal();
  const subs = client?.sub_clients || [];
  const isMain = subs.length > 0;
  const [params, setParams] = useSearchParams();
  const valid = (v) => v === "__self__" || subs.some((s) => s.id === v);
  const [selected, setSelectedState] = useState(params.get("client") || readFocus());
  useEffect(() => {
    const fromUrl = params.get("client") || "";
    if (fromUrl) { setSelectedState(fromUrl); writeFocus(fromUrl); return; }
    const remembered = readFocus();
    if (remembered && isMain && valid(remembered)) {
      setSelectedState(remembered);
      const next = new URLSearchParams(params); next.set("client", remembered); setParams(next, { replace: true });
    } else {
      setSelectedState("");
      if (remembered && isMain && !valid(remembered)) writeFocus("");  // stale focus from another account/family
    }
  }, [params, isMain]);  // eslint-disable-line react-hooks/exhaustive-deps
  const setSelected = (v) => {
    setSelectedState(v);
    writeFocus(v);
    const next = new URLSearchParams(params);
    if (v) next.set("client", v); else next.delete("client");
    setParams(next, { replace: true });
  };
  const matches = (item) => !isMain || !selected || (selected === "__self__" ? !item.for_client : item.client_id === selected);
  const selectedSub = subs.find((s) => s.id === selected) || null;
  return { isMain, subs, self: client, selected, setSelected, matches, selectedSub };
}

export function FamilyFilter({ fam, testid = "client-family-filter" }) {
  if (!fam?.isMain) return null;
  return (
    <div className="flex items-center gap-1.5">
      <select value={fam.selected} onChange={(e) => fam.setSelected(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={testid}>
        <option value="">All clients</option>
        <option value="__self__">{fam.self?.display_name || "Our own"}</option>
        {fam.subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
      </select>
      {fam.selectedSub && (
        <Link to={`/portal/sub-clients/${fam.selectedSub.id}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-border bg-card text-[11px] text-primary hover:bg-muted/40" title={`Open ${fam.selectedSub.display_name}'s overview`} data-testid={`${testid}-open-sub`}>
          <ArrowSquareOut size={12} /> Overview
        </Link>
      )}
      {fam.selected && (
        <button type="button" onClick={() => fam.setSelected("")} className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/40" title="Clear client focus (remembered across pages)" aria-label="Clear client focus" data-testid={`${testid}-clear`}>
          <X size={12} />
        </button>
      )}
    </div>
  );
}

/* Rows are usually whole-row <Link>s, so the chip is a span acting as a link (no nested anchors) that navigates to the
   sub-client's oversight page and stops the row click. */
export function ForClientChip({ name, clientId, testid }) {
  const navigate = useNavigate();
  if (!name) return null;
  const cls = "inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-900 uppercase tracking-wide max-w-[180px] truncate";
  if (clientId) {
    const go = (e) => { e.preventDefault(); e.stopPropagation(); navigate(`/portal/sub-clients/${clientId}`); };
    return (
      <span role="link" tabIndex={0} onClick={go} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(e); }} className={`${cls} hover:bg-sky-500/20 cursor-pointer`} title={`Raised for ${name} — open their overview`} data-testid={testid || "for-client-chip"}>
        <Buildings size={10} /> {name}
      </span>
    );
  }
  return (
    <span className={cls} title={`Raised for ${name}`} data-testid={testid || "for-client-chip"}>
      <Buildings size={10} /> {name}
    </span>
  );
}

export function OnBehalfSelect({ value, onChange, testid = "on-behalf-select" }) {
  const { client } = usePortal();
  const subs = client?.sub_clients || [];
  if (!subs.length) return null;
  return (
    <div>
      <label className="text-xs font-medium block mb-1">Which client is this for?</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={testid}>
        <option value="">{client.display_name} (ourselves)</option>
        {subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
      </select>
      <p className="text-[10px] text-muted-foreground mt-1">Raise it on behalf of a sub-client and it appears in their portal too, under your branding.</p>
    </div>
  );
}
